/**
 * Legacy Route Redirect
 *
 * Catches old flat routes (/, /calendar, /listings, etc.) and redirects them
 * to the new property-scoped workspace routes (/properties/:id/settings, etc.).
 *
 * Uses the stored activePropertyId from auth context (populated from localStorage)
 * to determine which property workspace to redirect to.
 *
 * Includes shadow-mode observability logging for pre-rollout analysis:
 * - Detects users without stored property context
 * - Captures route mapping for debugging
 */

import { Navigate, useLocation } from "react-router"
import { useAuth } from "@/lib/auth"

function stripSlashes(pathname: string): string {
  return pathname.replace(/^\/+/, "").replace(/\/+$/, "")
}

function mergeSearchParam(search: string, key: string, value: string): string {
  const qs = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search)
  qs.set(key, value)
  const next = qs.toString()
  return next ? `?${next}` : ""
}

function resolveLegacyTarget(pathname: string, search: string): { section: string; search: string } {
  const raw = stripSlashes(pathname)

  if (!raw) return { section: "", search }
  if (raw === "listings" || raw.startsWith("listings/")) return { section: "settings", search }
  if (raw === "calendar" || raw.startsWith("calendar/")) return { section: "settings", search }
  if (raw === "display") return { section: "display-behaviour", search }
  if (raw === "research") {
    return { section: "places", search: mergeSearchParam(search, "drawer", "research") }
  }
  if (raw === "status" || raw === "jobs" || raw === "audit") return { section: "", search: "" }

  const passthroughSections = new Set([
    "places",
    "house-rules",
    "display-behaviour",
    "guidebook",
    "settings",
  ])

  if (passthroughSections.has(raw)) {
    return { section: raw, search }
  }

  return { section: "", search: "" }
}

export function LegacyRouteRedirect() {
  const { activePropertyId, properties } = useAuth()
  const location = useLocation()

  // No stored property → go to directory
  if (!activePropertyId) {
    return <Navigate to="/properties" replace />
  }

  const target = resolveLegacyTarget(location.pathname, location.search)
  const newPath = `/properties/${activePropertyId}${target.section ? `/${target.section}` : ""}${target.search}`

  return <Navigate to={newPath} replace />
}
